import React, {Component} from 'react';
import PageHeader from "../PageHeader";
import Menu from "../Menu";
import Preloader from "../Preloader";
import api from "../../api";
import {getRecordComment} from "../Record/recordComment";
import './SalonSchedule.scss';

type SalonEvent = {
    id: number,
    time: string,
    endTime: string,
    durationMinutes: number,
    durationLabel: string,
    statusLabel: string,
    staff: { id: number, name: string },
    client?: { id: number, name: string } | null,
    serviceText: string,
    services: string[],
    commentRecord?: string | null,
}

type SalonStaff = {
    id: number,
    name: string,
    eventsCount: number,
}

type SalonCompany = {
    id: number,
    title: string,
    address: string,
}

export class SalonSchedule extends Component<any, any> {
    constructor(props: any) {
        super(props);
        window.scrollTo(0, 0);

        this.state = {
            isLoading: true,
            error: '',
            date: this.today(),
            companyId: 0,
            company: null as SalonCompany | null,
            companies: [] as SalonCompany[],
            staff: [] as SalonStaff[],
            events: [] as SalonEvent[],
            activeEvent: null as SalonEvent | null,
        };
    }

    componentDidMount() {
        this.loadSchedule();
    }

    today = () => {
        const date = new Date();
        const month = this.twoDigits(date.getMonth() + 1);
        const day = this.twoDigits(date.getDate());
        return `${date.getFullYear()}-${month}-${day}`;
    }

    twoDigits = (value: number): string => {
        return value < 10 ? `0${value}` : String(value);
    }

    loadSchedule = () => {
        this.setState({isLoading: true, error: ''});
        const params: any = {date: this.state.date};
        if (Number(this.state.companyId) > 0) {
            params.companyId = Number(this.state.companyId);
        }

        api({
            url: '/widget/staff/v2/salon-schedule',
            method: 'GET',
            params,
        }).then((result) => {
            this.setState({
                isLoading: false,
                company: result.data.company || null,
                companyId: result.data.company?.id || this.state.companyId,
                companies: Array.isArray(result.data.companies) ? result.data.companies : [],
                staff: Array.isArray(result.data.staff) ? result.data.staff : [],
                events: Array.isArray(result.data.events) ? result.data.events : [],
            });
        }).catch(() => {
            this.setState({
                isLoading: false,
                error: 'Не удалось загрузить расписание салона',
            });
        });
    }

    handleDateChange = (event: any) => {
        this.setState({date: event.target.value}, this.loadSchedule);
    }

    handleCompanyChange = (event: any) => {
        this.setState({companyId: Number(event.target.value)}, this.loadSchedule);
    }

    minutesFromTime = (time: string): number => {
        const parts = String(time || '').split(':');
        const hours = Number(parts[0] || 0);
        const minutes = Number(parts[1] || 0);
        return hours * 60 + minutes;
    }

    getTimelineBounds = () => {
        const events = this.state.events as SalonEvent[];
        if (!events.length) {
            return {start: 9 * 60, end: 21 * 60};
        }

        let min = 24 * 60;
        let max = 0;
        events.forEach((event) => {
            const start = this.minutesFromTime(event.time);
            const end = this.minutesFromTime(event.endTime) || start + (event.durationMinutes || 30);
            min = Math.min(min, start);
            max = Math.max(max, end);
        });

        const start = Math.max(0, Math.floor((min - 30) / 60) * 60);
        const end = Math.min(24 * 60, Math.ceil((max + 30) / 60) * 60);
        return {
            start: start >= end ? 9 * 60 : start,
            end: start >= end ? 21 * 60 : end,
        };
    }

    getHourMarks = (start: number, end: number): number[] => {
        const marks: number[] = [];
        for (let minute = start; minute <= end; minute += 60) {
            marks.push(minute);
        }
        return marks;
    }

    formatHour = (minute: number): string => {
        return `${this.twoDigits(Math.floor(minute / 60))}:00`;
    }

    eventsByStaff = () => {
        const map: {[key: string]: SalonEvent[]} = {};
        (this.state.events as SalonEvent[]).forEach((event) => {
            const staffId = String(event.staff?.id || 0);
            if (!map[staffId]) {
                map[staffId] = [];
            }
            map[staffId].push(event);
        });
        return map;
    }

    renderEventModal = () => {
        const event = this.state.activeEvent as SalonEvent | null;
        if (!event) {
            return null;
        }
        const comment = getRecordComment(event);

        return (
            <div className='salon-schedule-modal-backdrop' onClick={() => this.setState({activeEvent: null})}>
                <div className='salon-schedule-modal' onClick={(clickEvent) => clickEvent.stopPropagation()}>
                    <div className='salon-schedule-modal-title'>{event.time} - {event.endTime}</div>
                    <div className='salon-schedule-modal-line'>
                        <span>Мастер</span>
                        <strong>{event.staff?.name || 'Не указан'}</strong>
                    </div>
                    <div className='salon-schedule-modal-line'>
                        <span>Услуга</span>
                        <strong>{event.serviceText || 'Услуга не указана'}</strong>
                    </div>
                    {event.client?.name ? (
                        <div className='salon-schedule-modal-line'>
                            <span>Клиент</span>
                            <strong>{event.client.name}</strong>
                        </div>
                    ) : null}
                    <div className='salon-schedule-modal-line'>
                        <span>Длительность</span>
                        <strong>{event.durationLabel || `${event.durationMinutes} мин`}</strong>
                    </div>
                    {comment ? (
                        <div className='salon-schedule-modal-line salon-schedule-modal-comment'>
                            <span>Комментарий</span>
                            <strong>{comment}</strong>
                        </div>
                    ) : null}
                    <div className='salon-schedule-modal-status'>{event.statusLabel}</div>
                    <button type='button' className='salon-schedule-close' onClick={() => this.setState({activeEvent: null})}>
                        Закрыть
                    </button>
                </div>
            </div>
        );
    }

    render() {
        const bounds = this.getTimelineBounds();
        const totalMinutes = Math.max(60, bounds.end - bounds.start);
        const hourMarks = this.getHourMarks(bounds.start, bounds.end);
        const eventsByStaff = this.eventsByStaff();

        return (
            <div className='main salon-schedule-page'>
                <div className='content mx-w-content position-relative'>
                    <PageHeader
                        pageTitle='Расписание салона'
                        backBtnUrl='/'
                    />

                    <div className='pt-header'></div>
                    {this.renderEventModal()}

                    <div className='salon-schedule-controls ml-3 mr-3'>
                        <input
                            className='form-control salon-schedule-control'
                            type='date'
                            value={this.state.date}
                            onChange={this.handleDateChange}
                        />
                        {this.state.companies.length > 1 ? (
                            <select
                                className='form-select salon-schedule-control'
                                value={this.state.companyId}
                                onChange={this.handleCompanyChange}
                            >
                                {this.state.companies.map((company: SalonCompany) => (
                                    <option key={company.id} value={company.id}>
                                        {company.address || company.title}
                                    </option>
                                ))}
                            </select>
                        ) : null}
                    </div>

                    {this.state.company ? (
                        <div className='salon-schedule-summary ml-3 mr-3'>
                            <div>
                                <div className='salon-schedule-summary-title'>{this.state.company.address || this.state.company.title}</div>
                                <div className='salon-schedule-summary-meta'>{this.state.events.length} записей · {this.state.staff.length} мастеров</div>
                            </div>
                            <button type='button' className='salon-schedule-refresh' onClick={this.loadSchedule}>Обновить</button>
                        </div>
                    ) : null}

                    {this.state.error ? (
                        <div className='salon-schedule-empty ml-3 mr-3'>{this.state.error}</div>
                    ) : null}

                    {this.state.isLoading ? (
                        <Preloader></Preloader>
                    ) : (
                        <div className='salon-schedule-board ml-3 mr-3 mb-5'>
                            <div className='salon-schedule-scroll'>
                                <div className='salon-schedule-hours'>
                                    <div className='salon-schedule-staff-spacer'></div>
                                    <div className='salon-schedule-hours-line'>
                                        {hourMarks.map((mark) => (
                                            <span key={mark}>{this.formatHour(mark)}</span>
                                        ))}
                                    </div>
                                </div>

                                {this.state.staff.length === 0 ? (
                                    <div className='salon-schedule-empty'>Нет мастеров для отображения</div>
                                ) : null}

                                {(this.state.staff as SalonStaff[]).map((staff) => {
                                    const staffEvents = eventsByStaff[String(staff.id)] || [];
                                    return (
                                        <div className='salon-schedule-row' key={staff.id}>
                                            <div className='salon-schedule-staff-name'>
                                                <strong>{staff.name}</strong>
                                                <span>{staffEvents.length ? `${staffEvents.length} зап.` : 'свободно'}</span>
                                            </div>
                                            <div className='salon-schedule-track'>
                                                {hourMarks.map((mark) => (
                                                    <span
                                                        key={mark}
                                                        className='salon-schedule-tick'
                                                        style={{left: `${((mark - bounds.start) / totalMinutes) * 100}%`}}
                                                    />
                                                ))}
                                                {staffEvents.map((event) => {
                                                    const start = this.minutesFromTime(event.time);
                                                    const end = this.minutesFromTime(event.endTime) || start + (event.durationMinutes || 30);
                                                    const left = Math.max(0, ((start - bounds.start) / totalMinutes) * 100);
                                                    const width = Math.max(7, ((end - start) / totalMinutes) * 100);
                                                    return (
                                                        <button
                                                            key={event.id}
                                                            type='button'
                                                            className='salon-schedule-event'
                                                            style={{left: `${left}%`, width: `${Math.min(width, 100 - left)}%`}}
                                                            onClick={() => this.setState({activeEvent: event})}
                                                        >
                                                            <span>{event.time}</span>
                                                            <strong>{event.serviceText || 'Запись'}</strong>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
                <Menu></Menu>
            </div>
        );
    }
}

export default SalonSchedule;
